/**
 * Fase-2 modal: parent-delete-investeringen.
 *
 * Bij branch-switch levert de reconcile een lijst concepten die de parent
 * verwijderde maar waar deze branch nog branch-local relaties aan had hangen
 * (zie `detectParentDeletedInvestments` in de worker). Per concept kiest de
 * modelleur:
 *   - **Behouden** (keep) → resurrect als branch-object + relaties terug;
 *   - **Verwijderen** (accept) → de verwijdering accepteren (relaties blijven
 *     weg, gemarkeerd als branch-eigen verwijdering zodat de prompt niet
 *     terugkomt);
 *   - **Later** → modal sluiten zonder beslissen; komt bij de volgende
 *     branch-switch terug.
 *
 * State leeft in de UI-store (`parentDeletePrompts`), gevuld door `hydrate`.
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { getRegistry, resolveBilingual } from "../registry";
import { useSession } from "../state/session";
import { type ParentDeletePrompt, useUi } from "../state/ui";
import { useWorkspaceData } from "../state/workspace";

export function ParentDeletePromptModal() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const prompts = useUi((s) => s.parentDeletePrompts);
  const setPrompts = useUi((s) => s.setParentDeletePrompts);
  const wsId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  const [busy, setBusy] = useState<string | null>(null);
  const keptRef = useRef(false);

  if (prompts.length === 0 || !wsId || !branchId) return null;

  const reg = getRegistry();
  const typeLabel = (typeId: string): string => {
    const et = reg.getElementType(typeId);
    return (et ? resolveBilingual(et.label, lang) : "") || typeId;
  };
  const nameLabel = (p: ParentDeletePrompt): string =>
    resolveBilingual(p.label, lang) || typeLabel(p.typeId);

  const resolve = async (p: ParentDeletePrompt, action: "keep" | "accept") => {
    setBusy(p.conceptId);
    try {
      await api.resolveParentDelete(wsId, branchId, {
        conceptId: p.conceptId,
        action,
        relationIds: p.relationIds,
      });
      if (action === "keep") keptRef.current = true;
      const remaining = prompts.filter((x) => x.conceptId !== p.conceptId);
      setPrompts(remaining);
      // Pas na het laatste item herhydrateren — alleen nodig als er iets
      // geresurrect is (anders verandert er visueel niets).
      if (remaining.length === 0 && keptRef.current) {
        keptRef.current = false;
        await useWorkspaceData.getState().hydrate(wsId, branchId);
      }
    } catch {
      // Stil — bij een fout blijft het item in de lijst staan zodat de
      // modelleur 't opnieuw kan proberen.
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="ledger fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg rounded-lg border border-(--ld-control) bg-(--ld-surface) p-5 shadow-xl">
        <h2 className="font-semibold text-base text-(--ld-text)">{t("parentDelete.title")}</h2>
        <p className="mt-1 text-(--ld-muted) text-sm">{t("parentDelete.description")}</p>
        <ul className="mt-4 flex max-h-80 flex-col gap-2 overflow-y-auto">
          {prompts.map((p) => (
            <li key={p.conceptId} className="rounded border border-(--ld-border) p-3">
              <div className="font-medium text-(--ld-text) text-sm">
                {nameLabel(p)}{" "}
                <span className="font-normal text-(--ld-muted)">({typeLabel(p.typeId)})</span>
              </div>
              <div className="mt-0.5 text-(--ld-muted) text-xs">
                {t("parentDelete.usedIn", { count: p.relationIds.length })}
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  disabled={busy === p.conceptId}
                  className="rounded border border-transparent bg-(--ld-positive-bg) px-3 py-1 font-medium text-(--ld-positive-ink) text-sm hover:bg-(--ld-positive-bg) disabled:opacity-50"
                  onClick={() => resolve(p, "keep")}
                >
                  {t("parentDelete.keep")}
                </button>
                <button
                  type="button"
                  disabled={busy === p.conceptId}
                  className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1 text-(--ld-text) text-sm hover:bg-(--ld-raised) disabled:opacity-50"
                  onClick={() => resolve(p, "accept")}
                >
                  {t("parentDelete.accept")}
                </button>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1 text-(--ld-text) text-sm hover:bg-(--ld-raised)"
            onClick={() => setPrompts([])}
          >
            {t("parentDelete.later")}
          </button>
        </div>
      </div>
    </div>
  );
}
